import type { Chord } from '@tunasong/music-lib'
import { ChordLib, GuitarLib, ScaleLib } from '@tunasong/music-lib'
import type { NoteName, Scale } from '@tunasong/schemas'
import {HBox, Typography, Tab, Tabs} from '@tunasong/ui-lib'
import type { FC, SyntheticEvent } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { FindGuitarChord } from '../guitar/find-chord/index.js'
import { Note } from '../note/index.js'
import ChordLibrary from './chord-library.js'
import styles from './chord-chooser.module.css'

import type { HTMLAttributes } from 'react'

export interface ChordChooserProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  chord?: Chord
  scale: Scale | null

  onChange(chord: Chord): void
}

export const ChordChooser: FC<ChordChooserProps> = props => {
  const { chord, scale, onChange, ...divProps } = props

  const [currentNotes, setCurrentNotes] = useState<NoteName[]>(chord ? ChordLib.getNotes(chord) : [])

  const handleGuitarChord = (chord: GuitarLib.GuitarChord) => {
    /** onChange is a partial update, so we need to clear root and variant.   */
    onChange(chord)

    setCurrentNotes(ChordLib.getNotes(chord))
  }

  // const handleChangeRoot = (ev: SelectChangeEvent) => setRoot(ev.target.value)
  type TabValue = 'inscale' | 'all' | 'fretboard' | 'suggestions'
  const initialTab = scale ? 'inscale' : 'all'
  const [tab, setTab] = useState<TabValue>(initialTab)
  const handleTab = useCallback((_ev: SyntheticEvent | null, value: string | number) => {
    setTab(value as TabValue)
    setCurrentNotes([])
  }, [])
  const scaleName = scale ? ScaleLib.getName(scale) : 'No scale selected'

  useEffect(() => {
    if (!scale && tab === 'inscale') {
      setTab('all')
    }
  }, [scale, tab])

  return (
    <div {...divProps}>
      <HBox className={styles.headerBox}>
        {currentNotes ? (
          <div>
            {chord ? (
              <Typography variant="body2" component="span" className={styles.chordNameLabel}>
                Notes in {ChordLib.getName(chord)}:
              </Typography>
            ) : null}
            {currentNotes.map((n, idx) => (
              <Note className={styles.noteChip} key={idx} note={n} scale={scale} />
            ))}
          </div>
        ) : (
          <Typography variant="body2">N/A</Typography>
        )}
      </HBox>
      <Tabs className={styles.tabs} value={tab} onChange={handleTab}>
        <Tab label={scaleName} value={'inscale'} />
        <Tab label={`All Chords`} value={'all'} />
        <Tab label={`Fretboard`} value={'fretboard'} />
      </Tabs>
      {tab === 'inscale' ? (
        <ChordLibrary
          selected={chord}
          scale={scale}
          chords={ScaleLib.getAllChords(scale)}
          onChange={handleGuitarChord}
        />
      ) : tab === 'all' ? (
        <ChordLibrary selected={chord} scale={scale} onChange={handleGuitarChord} />
      ) : tab === 'fretboard' ? (
        <FindGuitarChord
          className={styles.fretboard}
          defaultChord={GuitarLib.isGuitarChord(chord) ? chord : undefined}
          scale={scale}
          onSelected={handleGuitarChord}
        />
      ) : tab === 'suggestions' ? (
        'Suggestions are coming soon!'
      ) : null}
    </div>
  )
}

export default ChordChooser
